/**
 * Signatures collectées hors session authentifiée (ex. tunnel diagnostic / offres)
 * puis rejouées via POST /api/templates/sign/ comme EntrerEnRelation.
 *
 * Ancienne logique (référence) :
 * 1. GET /api/documents-a-signer/ → listes CGU (dès connexion) + contrat (après client_apte)
 * 2. POST /api/templates/sign/ { titre, signature_image, timestamp }
 * 3. Backend charge le PDF template, fusionne la signature, upload Azure, crée/maj Signature
 *    (unique_together client + document_template). Le contrat de souscription est refusé
 *    tant que client_apte est faux.
 */
import axios from 'axios';

export const PENDING_SIGNATURES_KEY = 'patrimonea_pending_signatures_v1';

export function loadPendingSignatures() {
  try {
    const raw = localStorage.getItem(PENDING_SIGNATURES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

export function savePendingSignatures(list) {
  try {
    localStorage.setItem(PENDING_SIGNATURES_KEY, JSON.stringify(list || []));
  } catch (_) {
    /* ignore */
  }
}

export function enqueuePendingSignature({
  titre,
  kind = 'souscription',
  signature_image,
  timestamp,
  email = '',
}) {
  if (!signature_image || !titre) return;
  const list = loadPendingSignatures().filter(
    (item) => String(item.titre || '').toLowerCase() !== String(titre).toLowerCase(),
  );
  list.push({
    titre: String(titre).trim(),
    kind: String(kind || 'souscription'),
    signature_image,
    timestamp: timestamp || Date.now(),
    email: String(email || '').trim().toLowerCase(),
    ts: Date.now(),
  });
  savePendingSignatures(list);
}

/**
 * Rejoue les signatures en attente. Pour un contrat `souscription`, n’essaie que si
 * clientApte === true (même règle serveur).
 */
export async function flushPendingSignatures({ clientApte = false, email = '' } = {}) {
  const pending = loadPendingSignatures();
  if (!pending.length) return { ok: [], failed: [] };

  const emailN = String(email || '').trim().toLowerCase();
  const remaining = [];
  const ok = [];
  const failed = [];

  for (const item of pending) {
    if (item.email && emailN && item.email !== emailN) {
      remaining.push(item);
      continue;
    }
    if (item.kind === 'souscription' && !clientApte) {
      remaining.push(item);
      continue;
    }
    try {
      await axios.post(
        '/api/templates/sign/',
        {
          titre: item.titre,
          kind: item.kind,
          signature_image: item.signature_image,
          timestamp: item.timestamp || Date.now(),
        },
        { withCredentials: true, timeout: 30000 },
      );
      ok.push(item.titre);
    } catch (e) {
      const code = e?.response?.data?.code;
      if (code === 'signature_after_payment_required') {
        remaining.push(item);
      } else {
        failed.push({ titre: item.titre, error: e });
        remaining.push(item);
      }
    }
  }

  savePendingSignatures(remaining);
  return { ok, failed };
}
